import { formatarPreco } from './priceUtils';
import { capitalizeWords, truncateText } from './formatUtils';

/**
 * Formata o valor de receita para exibição
 * @param {number} value - Valor a ser formatado
 * @returns {string} - Valor formatado com símbolo de moeda
 */
export const formatRevenue = (value) => {
  if (!value && value !== 0) return '-';
  return formatarPreco(value);
};

/**
 * Formata a taxa de crescimento para exibição com sinal
 * @param {number} value - Valor percentual
 * @returns {string} - Valor formatado com sinal + ou -
 */
export const formatGrowth = (value) => {
  if (!value && value !== 0) return '-';
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${formatPercentage(value)}`;
};

/**
 * Formata um valor percentual
 * @param {number} value - Valor a ser formatado
 * @returns {string} - Valor formatado com símbolo de porcentagem
 */
export const formatPercentage = (value) => {
  if (!value && value !== 0) return '-';
  return `${value}%`;
};

/**
 * Formata a duração em minutos para exibição em horas e minutos
 * @param {number} minutes - Duração em minutos
 * @returns {string} - Duração formatada
 */
export const formatAppointmentDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '-';
  return formatDuration(minutes * 60); // Convertendo minutos para segundos para usar formatDuration
};

/**
 * Formata a duração em segundos para exibição
 * @param {number} seconds - Duração em segundos
 * @returns {string} - Duração formatada
 */
export const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '-';
  
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Retorna os dias da semana em formato curto
 * @returns {Array} - Array com os dias da semana
 */
export const getWeekDays = () => {
  return ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
};

/**
 * Retorna as semanas do mês em formato curto
 * @returns {Array} - Array com as semanas do mês
 */
export const getMonthWeeks = () => {
  return ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4'];
};

/**
 * Gera dados simulados para gráfico de agendamentos por período
 * @param {string} period - Período (day, week, month)
 * @returns {Object} - Dados formatados para Chart.js
 */
export const generateAppointmentsChartData = (period) => {
  let labels = [];
  let datasets = [];

  switch (period) {
    case 'hour':
      labels = Array.from({ length: 12 }, (_, i) => `${i + 8}h`);
      datasets = [
        {
          label: 'Agendamentos',
          data: Array.from({ length: 12 }, () => Math.floor(Math.random() * 10) + 1),
          backgroundColor: 'rgba(99, 102, 241, 0.7)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 1,
          borderRadius: 4,
        }
      ];
      break;
    case 'week':
      labels = getWeekDays();
      datasets = [
        {
          label: 'Agendamentos',
          data: Array.from({ length: 7 }, () => Math.floor(Math.random() * 20) + 5),
          backgroundColor: 'rgba(99, 102, 241, 0.7)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: 'Comparecimentos',
          data: Array.from({ length: 7 }, (_, i) => Math.floor(Math.random() * (15 - 3 + 1) + 3)),
          backgroundColor: 'rgba(34, 197, 94, 0.7)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 1,
          borderRadius: 4,
        }
      ];
      break;
    case 'month':
      labels = getMonthWeeks();
      datasets = [
        {
          label: 'Agendamentos',
          data: Array.from({ length: 4 }, () => Math.floor(Math.random() * 30) + 30),
          backgroundColor: 'rgba(99, 102, 241, 0.7)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: 'Comparecimentos',
          data: Array.from({ length: 4 }, () => Math.floor(Math.random() * 20) + 25),
          backgroundColor: 'rgba(34, 197, 94, 0.7)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 1,
          borderRadius: 4,
        }
      ];
      break;
    default:
      labels = getWeekDays();
      datasets = [
        {
          label: 'Agendamentos',
          data: Array.from({ length: 7 }, () => Math.floor(Math.random() * 20) + 5),
          backgroundColor: 'rgba(99, 102, 241, 0.7)'
        }
      ];
  }

  return { labels, datasets };
};

/**
 * Calcula a porcentagem de um valor em relação ao total
 * @param {number} value - Valor parcial
 * @param {number} total - Valor total
 * @returns {number} - Porcentagem calculada (0-100)
 */
export const calculatePercentage = (value, total) => {
  if (!value || !total) return 0;
  return Math.round((value / total) * 100);
};

/**
 * Determina a tendência com base na comparação de valores
 * @param {number} current - Valor atual
 * @param {number} previous - Valor anterior
 * @returns {Object} - Objeto com informações de tendência
 */
export const getTrend = (current, previous) => {
  if (!current || !previous) return { value: 0, isPositive: true };
  
  const diff = current - previous;
  const percentage = Math.round((diff / previous) * 100);
  
  return {
    value: Math.abs(percentage),
    isPositive: percentage >= 0,
    text: formatGrowth(percentage)
  };
};

/**
 * Formata o número de acordo com a localização
 * @param {number} value - Valor a ser formatado
 * @returns {string} - Valor formatado
 */
export const formatPtBrNumber = (value) => {
  if (!value && value !== 0) return '-';
  return formatNumber(value);
};

/**
 * Formata um número para exibição
 * @param {number} value - Valor a ser formatado
 * @returns {string} - Valor formatado
 */
export const formatNumber = (value) => {
  if (!value && value !== 0) return '-';
  return value.toLocaleString('pt-BR');
};

/**
 * Formata a avaliação para exibição
 * @param {number} rating - Valor da avaliação
 * @returns {string} - Avaliação formatada com 1 casa decimal
 */
export const formatRating = (rating) => {
  if (!rating && rating !== 0) return '-';
  return rating.toFixed(1);
};

/**
 * Obtém a cor com base na tendência do valor
 * @param {number} value - Valor da tendência
 * @returns {string} - Nome da cor (green, red, yellow)
 */
export const getTrendColor = (value) => {
  if (value > 0) return 'green';
  if (value < 0) return 'red';
  return 'yellow';
};

export default {
  formatRevenue,
  formatGrowth,
  formatAppointmentDuration,
  generateAppointmentsChartData,
  calculatePercentage,
  getTrend,
  formatPtBrNumber,
  formatRating,
  getTrendColor,
  formatPercentage,
  formatDuration,
  formatNumber
}; 